import { Suspense, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment, useMutation } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import { formatDurationTimestamp } from '@/utils/format';
import type { EpisodesPageQuery } from '@/__generated__/EpisodesPageQuery.graphql';
import type { EpisodesPageTopicsQuery } from '@/__generated__/EpisodesPageTopicsQuery.graphql';
import type { EpisodesPage_topic$key } from '@/__generated__/EpisodesPage_topic.graphql';
import type { EpisodesPageDeleteMutation } from '@/__generated__/EpisodesPageDeleteMutation.graphql';

/**
 * Query to fetch all topics for the selector
 */
const topicsQuery = graphql`
  query EpisodesPageTopicsQuery {
    topics(first: 100) {
      edges {
        node {
          id
          slug
          name
          episodeCount
        }
      }
    }
  }
`;

/**
 * Query to fetch episodes for a specific topic
 */
const episodesPageQuery = graphql`
  query EpisodesPageQuery($slug: String!) {
    topic(slug: $slug) {
      ...EpisodesPage_topic
    }
  }
`;

/**
 * Fragment for topic data with paginated episodes
 */
const EpisodesPage_topic = graphql`
  fragment EpisodesPage_topic on Topic
  @refetchable(queryName: "EpisodesPageRefetchQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 20 }
    after: { type: "String" }
  ) {
    id
    slug
    name
    episodeCount
    episodes(first: $first, after: $after)
      @connection(key: "EpisodesPage_episodes") {
      edges {
        node {
          id
          episodeId
          title
          publishedAt
          durationSeconds
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/**
 * Mutation to delete an episode
 */
const deleteEpisodeMutation = graphql`
  mutation EpisodesPageDeleteMutation($input: DeleteEpisodeInput!, $connections: [ID!]!) {
    deleteEpisode(input: $input) {
      deletedEpisodeId @deleteEdge(connections: $connections)
      topic {
        id
        episodeCount
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface TopicSelectorProps {
  selectedSlug: string;
  onSelect: (slug: string) => void;
}

function TopicSelector({ selectedSlug, onSelect }: TopicSelectorProps) {
  const data = useLazyLoadQuery<EpisodesPageTopicsQuery>(topicsQuery, {});
  const [topicSearch, setTopicSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const topics = data.topics.edges.map((e) => e.node);

  const selectedTopic = useMemo(
    () => topics.find((t) => t.slug === selectedSlug),
    [topics, selectedSlug]
  );

  const filteredTopics = useMemo(() => {
    if (!topicSearch) return topics;
    const search = topicSearch.toLowerCase();
    return topics.filter(
      (t) =>
        t.name.toLowerCase().includes(search) ||
        t.slug.toLowerCase().includes(search)
    );
  }, [topics, topicSearch]);

  return (
    <div className="mb-6">
      <label
        htmlFor="topic-search"
        className="block text-sm font-medium text-gray-300 mb-2"
      >
        Select Topic
      </label>
      <div className="relative">
        <input
          id="topic-search"
          type="text"
          className="input w-full max-w-md"
          placeholder="Search topics..."
          value={isDropdownOpen ? topicSearch : (selectedTopic?.name ?? topicSearch)}
          onChange={(e) => {
            setTopicSearch(e.target.value);
            setIsDropdownOpen(true);
          }}
          onFocus={() => {
            setIsDropdownOpen(true);
            setTopicSearch('');
          }}
        />
        {isDropdownOpen && filteredTopics.length > 0 && (
          <div className="absolute z-10 mt-1 w-full max-w-md rounded-xl border border-gray-700 bg-gray-800 shadow-lg max-h-60 overflow-y-auto">
            {filteredTopics.map((topic) => (
              <button
                key={topic.slug}
                type="button"
                className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white flex items-center justify-between"
                onClick={() => {
                  onSelect(topic.slug);
                  setTopicSearch('');
                  setIsDropdownOpen(false);
                }}
              >
                <span>{topic.name}</span>
                <span className="text-xs text-gray-500">
                  {topic.episodeCount} episode{topic.episodeCount !== 1 ? 's' : ''}
                </span>
              </button>
            ))}
          </div>
        )}
        {isDropdownOpen && filteredTopics.length === 0 && topicSearch && (
          <div className="absolute z-10 mt-1 w-full max-w-md rounded-xl border border-gray-700 bg-gray-800 shadow-lg p-4 text-sm text-gray-400">
            No topics found matching "{topicSearch}"
          </div>
        )}
      </div>
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsDropdownOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsDropdownOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close dropdown"
        />
      )}
    </div>
  );
}

interface EpisodeListProps {
  topicRef: EpisodesPage_topic$key;
}

function EpisodeList({ topicRef }: EpisodeListProps) {
  const { data: topic, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    EpisodesPage_topic,
    topicRef
  );
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [commitDelete, isDeleting] = useMutation<EpisodesPageDeleteMutation>(deleteEpisodeMutation);

  // Use ConnectionHandler to get the proper connection ID
  const connectionId = ConnectionHandler.getConnectionID(topic.id, 'EpisodesPage_episodes');

  const episodes = topic.episodes.edges.map((e) => e.node);

  const handleDelete = (episodeId: string) => {
    commitDelete({
      variables: {
        input: {
          topicSlug: topic.slug,
          episodeId,
        },
        connections: [connectionId],
      },
      onCompleted: () => {
        setDeleteConfirm(null);
      },
    });
  };

  if (episodes.length === 0) {
    return (
      <div className="card text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
          />
        </svg>
        <p className="mt-4 text-gray-400">No episodes yet for this topic</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">
          {topic.name}
          <span className="ml-2 text-sm font-normal text-gray-400">
            ({topic.episodeCount} episode{topic.episodeCount !== 1 ? 's' : ''})
          </span>
        </h3>
      </div>

      <div className="space-y-3">
        {episodes.map((episode) => (
          <div
            key={episode.episodeId}
            className="card group transition-all hover:border-gray-700"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white truncate">{episode.title}</h4>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
                  <span>{formatDate(episode.publishedAt)}</span>
                  <span>{formatDurationTimestamp(episode.durationSeconds)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/admin/episodes/${topic.slug}/${episode.episodeId}`}
                  className="btn-secondary text-xs"
                >
                  View
                </Link>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(episode.episodeId)}
                  className="btn-secondary text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  Delete
                </button>
              </div>
            </div>

            {deleteConfirm === episode.episodeId && (
              <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                <p className="text-sm text-red-200">
                  Are you sure you want to delete{' '}
                  <strong className="text-white">{episode.title}</strong>? This
                  action cannot be undone.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleDelete(episode.episodeId)}
                    className="btn-danger"
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="btn-secondary"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {hasNext && (
          <div className="flex justify-center pt-4">
            <button
              type="button"
              onClick={() => loadNext(20)}
              disabled={isLoadingNext}
              className="btn-secondary"
            >
              {isLoadingNext ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function EpisodesPageContent({ slug }: { slug: string }) {
  const data = useLazyLoadQuery<EpisodesPageQuery>(episodesPageQuery, { slug });

  if (!data.topic) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">Topic not found</p>
      </div>
    );
  }

  return <EpisodeList topicRef={data.topic} />;
}

export function EpisodesPage() {
  const { topicSlug: initialSlug } = useParams<{ topicSlug?: string }>();
  const navigate = useNavigate();
  const [selectedSlug, setSelectedSlug] = useState<string>(initialSlug ?? '');

  const handleTopicSelect = (slug: string) => {
    setSelectedSlug(slug);
    navigate(`/admin/episodes/${slug}`, { replace: true });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">Episodes</h2>
        <p className="mt-1 text-gray-400">
          View and manage episodes for your podcast topics
        </p>
      </div>

      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        }
      >
        <TopicSelector selectedSlug={selectedSlug} onSelect={handleTopicSelect} />
      </Suspense>

      {selectedSlug ? (
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            </div>
          }
        >
          <EpisodesPageContent slug={selectedSlug} />
        </Suspense>
      ) : (
        <div className="card text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
            />
          </svg>
          <p className="mt-4 text-gray-400">Select a topic to view its episodes</p>
        </div>
      )}
    </div>
  );
}
